import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"

import {
    Avatar,
    Badge,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Skeleton,
    Snackbar,
    Alert,
    Stack,
    TextField,
    Typography,
    Switch,
    FormControlLabel,
    Tooltip,
    Drawer,
    CircularProgress,
} from "@mui/material"

import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined"
import PersonOutlineIcon from "@mui/icons-material/PersonOutline"
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined"
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined"
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined"
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined"
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined"
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined"
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined"
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined"
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined"
import SearchIcon from "@mui/icons-material/Search"
import InputAdornment from "@mui/material/InputAdornment"

import { rtdb, auth, storage } from "../services/firebase"
import { ref as dbRef, onValue, update, push, remove } from "firebase/database"
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage"
import { signOut } from "firebase/auth"
import { getUserOrders } from "../services/orderService"
import { buscarCEP } from "../services/cepService"

/**
 * MinhaConta (Client)
 * - RTDB: users/client/{uid}
 * - Storage: profileImages/{uid}/{timestamp}.jpg
 */

const MENU = [
    { key: "overview", label: "Visão geral", icon: <PersonOutlineIcon /> },
    { key: "profile", label: "Dados pessoais", icon: <PersonOutlineIcon /> },
    { key: "address", label: "Endereço", icon: <HomeOutlinedIcon /> },
    { key: "orders", label: "Meus pedidos", icon: <ShoppingBagOutlinedIcon /> },
    { key: "security", label: "Segurança", icon: <LockOutlinedIcon /> },
    { key: "prefs", label: "Preferências", icon: <TuneOutlinedIcon /> },
    { key: "support", label: "Suporte", icon: <SupportAgentOutlinedIcon /> },
]

const textFieldSx = {
    "& .MuiInputLabel-root": { color: "rgba(0,0,0,0.55)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "rgba(0,0,0,0.75)" },
    "& .MuiInputLabel-root.MuiInputLabel-shrink": { color: "rgba(0,0,0,0.55)" },
    "& .MuiFormHelperText-root": { color: "rgba(0,0,0,0.55)" },
}

const cardSx = {
    borderRadius: 4,
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: {
        xs: "0 8px 24px rgba(0,0,0,0.06)",
        md: "0 16px 40px rgba(0,0,0,0.08)",
    },
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
}

const softCardSx = {
    borderRadius: 4,
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: {
        xs: "0 6px 18px rgba(0,0,0,0.06)",
        md: "0 12px 30px rgba(0,0,0,0.06)",
    },
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
}

function sanitizePhone(value = "") {
    const digits = String(value).replace(/\D/g, "")
    return digits.slice(0, 13)
}

function sanitizeZip(value = "") {
    const digits = String(value).replace(/\D/g, "")
    return digits.slice(0, 8)
}

function formatZip(zip = "") {
    const d = sanitizeZip(zip)
    if (d.length <= 5) return d
    return `${d.slice(0, 5)}-${d.slice(5)}`
}

function copyToClipboard(text) {
    try {
        navigator.clipboard.writeText(text)
        return true
    } catch {
        return false
    }
}

export default function MinhaConta() {
    const navigate = useNavigate()

    const [active, setActive] = useState("overview")
    const [loading, setLoading] = useState(true)
    const [client, setClient] = useState(null)

    const [snack, setSnack] = useState({
        open: false,
        type: "success",
        message: "",
    })

    const fileRef = useRef(null)
    const [uploadingPhoto, setUploadingPhoto] = useState(false)

    // dialogs
    const [openEditProfile, setOpenEditProfile] = useState(false)
    const [openEditAddress, setOpenEditAddress] = useState(false)
    const [editingAddressId, setEditingAddressId] = useState(null)

    // forms
    const [profileForm, setProfileForm] = useState({
        name: "",
        email: "",
        phone: "",
        documentType: "CPF",
        documentValue: "",
        birthDate: "",
    })

    const [addressForm, setAddressForm] = useState({
        label: "Casa",
        recipientName: "",
        phone: "",
        street: "",
        number: "",
        complement: "",
        neighborhood: "",
        city: "",
        state: "",
        zipCode: "",
        country: "BR",
        referencePoint: "",
        isDefault: false,
    })

    const [addresses, setAddresses] = useState([])
    const [loadingCEP, setLoadingCEP] = useState(false)
    const [lastSearchedCEP, setLastSearchedCEP] = useState("")

    const [prefs, setPrefs] = useState({
        marketing: true,
        orderUpdates: true,
        whatsapp: true,
    })

    const [mobileOpen, setMobileOpen] = useState(false)
    const toggleMobile = () => setMobileOpen((v) => !v)
    const [userOrders, setUserOrders] = useState([])
    const [loadingOrders, setLoadingOrders] = useState(false)

    const uid = useMemo(() => {
        const ls = (() => {
            try {
                return JSON.parse(localStorage.getItem("emmaUser") || "null")
            } catch {
                return null
            }
        })()

        return ls?.uid || ls?.userId || ls?.id || auth?.currentUser?.uid || null
    }, [])

    const clientPath = useMemo(() => {
        if (!uid) return null
        return `users/client/${uid}`
    }, [uid])

    function toast(type, message) {
        setSnack({ open: true, type, message })
    }

    useEffect(() => {
        if (!clientPath) {
            setLoading(false)
            setClient(null)
            return
        }

        const r = dbRef(rtdb, clientPath)

        const unsub = onValue(r, (snap) => {
            const data = snap.val()
            if (!data) return

            setClient(data)

            const fullName = `${data.firstName || ""} ${data.lastName || ""}`.trim()

            setProfileForm({
                name: fullName,
                email: data.email || "",
                phone: sanitizePhone(data.phone || ""),
                documentType: "CPF",
                documentValue: data.cpf || "",
                birthDate: data.birthDate || "",
            })

            // Carregar endereços (suporte a múltiplos endereços)
            let addressesList = []
            if (data.addresses) {
                addressesList = Object.keys(data.addresses).map(key => ({
                    id: key,
                    ...data.addresses[key]
                }))
            } else if (data.address) {
                // Compatibilidade com formato antigo (endereço único)
                addressesList = [{
                    id: "default",
                    ...data.address,
                    isDefault: true
                }]
            }
            setAddresses(addressesList)

            // Se houver endereço padrão, preencher o form com ele
            const defaultAddress = addressesList.find(addr => addr.isDefault) || addressesList[0]
            if (defaultAddress) {
                setAddressForm({
                    label: defaultAddress.label || "Casa",
                    recipientName: defaultAddress.recipientName || "",
                    phone: sanitizePhone(defaultAddress.phone || ""),
                    street: defaultAddress.street || "",
                    number: defaultAddress.number || "",
                    complement: defaultAddress.complement || "",
                    neighborhood: defaultAddress.neighborhood || "",
                    city: defaultAddress.city || "",
                    state: defaultAddress.state || "",
                    zipCode: defaultAddress.zipCode || "",
                    country: defaultAddress.country || "BR",
                    referencePoint: defaultAddress.referencePoint || "",
                    isDefault: defaultAddress.isDefault || false,
                })
            } else {
                // Reset form se não houver endereços
                setAddressForm({
                    label: "Casa",
                    recipientName: "",
                    phone: "",
                    street: "",
                    number: "",
                    complement: "",
                    neighborhood: "",
                    city: "",
                    state: "",
                    zipCode: "",
                    country: "BR",
                    referencePoint: "",
                    isDefault: false,
                })
            }

            setLoading(false)
        })

        return () => unsub()
    }, [clientPath])

    // Carregar pedidos do usuário
    useEffect(() => {
        async function loadOrders() {
            if (!uid) return;
            setLoadingOrders(true);
            try {
                const orders = await getUserOrders(uid);
                setUserOrders(orders);
            } catch (err) {
                console.error("Erro ao carregar pedidos:", err);
            } finally {
                setLoadingOrders(false);
            }
        }
        loadOrders();
    }, [uid])

    // Buscar CEP automaticamente quando tiver 8 dígitos no modal de endereço
    useEffect(() => {
        if (!openEditAddress) {
            setLastSearchedCEP("")
            return
        }

        const cepLimpo = addressForm.zipCode?.replace(/\D/g, "") || ""
        
        // Só busca se tiver 8 dígitos e não for o mesmo CEP já buscado
        if (cepLimpo.length === 8 && cepLimpo !== lastSearchedCEP) {
            buscarEnderecoPorCEP(cepLimpo)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressForm.zipCode, openEditAddress])

    async function buscarEnderecoPorCEP(cep) {
        setLoadingCEP(true)
        setLastSearchedCEP(cep)

        const resultado = await buscarCEP(cep)

        if (resultado.ok && resultado.data) {
            setAddressForm(prev => ({
                ...prev,
                zipCode: resultado.data.cep,
                street: resultado.data.street,
                neighborhood: resultado.data.district,
                city: resultado.data.city,
                state: resultado.data.state,
                // Mantém complemento se já existir, mas não preenche automaticamente
                complement: prev.complement || "",
            }))
        }

        setLoadingCEP(false)
    }

    async function handleLogout() {
        try {
            await signOut(auth)
            localStorage.removeItem("emmaUser")
            navigate("/auth")
        } catch {
            toast("error", "Não foi possível sair agora.")
        }
    }

    async function saveProfile() {
        if (!clientPath) return

        const name = profileForm.name.trim()
        if (name.length < 2) {
            return toast("error", "Nome inválido.")
        }

        const [firstName, ...rest] = name.split(" ")
        const lastName = rest.join(" ")

        try {
            await update(dbRef(rtdb, clientPath), {
                firstName,
                lastName,
                phone: sanitizePhone(profileForm.phone),
            })

            setOpenEditProfile(false)
            toast("success", "Dados pessoais atualizados!")
        } catch {
            toast("error", "Erro ao salvar seus dados.")
        }
    }

    async function saveAddress() {
        if (!clientPath) return

        const payload = {
            label: (addressForm.label || "Casa").slice(0, 20),
            recipientName: (addressForm.recipientName || "").slice(0, 60),
            phone: sanitizePhone(addressForm.phone),
            street: (addressForm.street || "").slice(0, 80),
            number: String(addressForm.number || "").slice(0, 10),
            complement: (addressForm.complement || "").slice(0, 60),
            neighborhood: (addressForm.neighborhood || "").slice(0, 60),
            city: (addressForm.city || "").slice(0, 60),
            state: (addressForm.state || "").slice(0, 30),
            zipCode: sanitizeZip(addressForm.zipCode),
            country: (addressForm.country || "BR").slice(0, 2),
            referencePoint: (addressForm.referencePoint || "").slice(0, 80),
            isDefault: !!addressForm.isDefault,
        }

        if (!payload.street || !payload.city || payload.zipCode.length < 8) {
            return toast("error", "Preencha ao menos Rua, Cidade e CEP válido.")
        }

        try {
            const addressesRef = dbRef(rtdb, `${clientPath}/addresses`)
            
            // Se está marcando como padrão, remover isDefault de todos os outros
            if (payload.isDefault) {
                const updates = {}
                addresses.forEach(addr => {
                    if (addr.id !== editingAddressId && addr.isDefault) {
                        updates[`${clientPath}/addresses/${addr.id}/isDefault`] = false
                    }
                })
                if (Object.keys(updates).length > 0) {
                    await update(dbRef(rtdb, "/"), updates)
                }
            }

            if (editingAddressId) {
                // Atualizar endereço existente
                await update(dbRef(rtdb, `${clientPath}/addresses/${editingAddressId}`), payload)
            } else {
                // Criar novo endereço
                const newAddressRef = push(addressesRef)
                await update(newAddressRef, payload)
                
                // Se for o primeiro endereço ou se marcou como padrão, definir como padrão
                if (addresses.length === 0 || payload.isDefault) {
                    await update(newAddressRef, { isDefault: true })
                }
            }

            setOpenEditAddress(false)
            setEditingAddressId(null)
            setAddressForm({
                label: "Casa",
                recipientName: "",
                phone: "",
                street: "",
                number: "",
                complement: "",
                neighborhood: "",
                city: "",
                state: "",
                zipCode: "",
                country: "BR",
                referencePoint: "",
                isDefault: false,
            })
            toast("success", editingAddressId ? "Endereço atualizado!" : "Endereço adicionado!")
        } catch (err) {
            console.error("Erro ao salvar endereço:", err)
            toast("error", "Erro ao salvar endereço.")
        }
    }

    async function deleteAddress(addressId) {
        if (!clientPath) return
        if (!window.confirm("Tem certeza que deseja excluir este endereço?")) return

        try {
            await remove(dbRef(rtdb, `${clientPath}/addresses/${addressId}`))
            toast("success", "Endereço excluído!")
        } catch (err) {
            console.error("Erro ao excluir endereço:", err)
            toast("error", "Erro ao excluir endereço.")
        }
    }

    async function setDefaultAddress(addressId) {
        if (!clientPath) return

        try {
            const updates = {}
            // Remover isDefault de todos
            addresses.forEach(addr => {
                if (addr.isDefault) {
                    updates[`${clientPath}/addresses/${addr.id}/isDefault`] = false
                }
            })
            // Definir novo padrão
            updates[`${clientPath}/addresses/${addressId}/isDefault`] = true
            
            await update(dbRef(rtdb, "/"), updates)
            toast("success", "Endereço padrão atualizado!")
        } catch (err) {
            console.error("Erro ao definir endereço padrão:", err)
            toast("error", "Erro ao definir endereço padrão.")
        }
    }

    function handleEditAddress(address) {
        if (address) {
            setAddressForm({
                label: address.label || "Casa",
                recipientName: address.recipientName || "",
                phone: sanitizePhone(address.phone || ""),
                street: address.street || "",
                number: address.number || "",
                complement: address.complement || "",
                neighborhood: address.neighborhood || "",
                city: address.city || "",
                state: address.state || "",
                zipCode: address.zipCode || "",
                country: address.country || "BR",
                referencePoint: address.referencePoint || "",
                isDefault: address.isDefault || false,
            })
            setEditingAddressId(address.id)
        } else {
            setAddressForm({
                label: "Casa",
                recipientName: "",
                phone: "",
                street: "",
                number: "",
                complement: "",
                neighborhood: "",
                city: "",
                state: "",
                zipCode: "",
                country: "BR",
                referencePoint: "",
                isDefault: addresses.length === 0, // Primeiro endereço é padrão automaticamente
            })
            setEditingAddressId(null)
        }
        setOpenEditAddress(true)
    }

    async function handlePickPhoto(file) {
        if (!file || !uid) return

        const isImage = file.type?.startsWith("image/")
        if (!isImage) return toast("error", "Envie uma imagem válida.")

        try {
            setUploadingPhoto(true)

            const filename = `${Date.now()}_${file.name}`.replace(/\s+/g, "_")
            const path = `profileImages/${uid}/${filename}`
            const sref = storageRef(storage, path)

            const task = uploadBytesResumable(sref, file)

            await new Promise((resolve, reject) => {
                task.on(
                    "state_changed",
                    () => { },
                    (err) => reject(err),
                    () => resolve(true),
                )
            })

            const url = await getDownloadURL(sref)

            await update(dbRef(rtdb, clientPath), {
                profileImage: url,
            })

            toast("success", "Foto atualizada!")
        } catch {
            toast("error", "Erro ao enviar foto.")
        } finally {
            setUploadingPhoto(false)
            if (fileRef.current) fileRef.current.value = ""
        }
    }

    const displayName =
        `${client?.firstName || ""} ${client?.lastName || ""}`.trim() || "Minha Conta"
    const displayEmail = client?.email || profileForm.email || ""
    const status = client?.status || "active"
    const role = client?.role || "client"
    const profileImage = client?.profileImage || ""
    const address = client?.address || null

    function SectionHeader({ title, subtitle, action }) {
        return (
            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "flex-start" }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: { xs: 2, md: 2.5 } }}
            >
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: -0.6, fontSize: { xs: 20, md: 24 } }}>
                        {title}
                    </Typography>
                    {subtitle ? (
                        <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.5 }}>
                            {subtitle}
                        </Typography>
                    ) : null}
                </Box>
                {action ? (
                    <Box
                        sx={{
                            width: { xs: "100%", sm: "auto" },
                            "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
                        }}
                    >
                        {action}
                    </Box>
                ) : null}
            </Stack>
        )
    }

    const sidebar = (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 2.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: -0.5 }}>
                    Minha Conta
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
                    Gerencie seus dados, endereço e preferências
                </Typography>
            </Box>

            <Divider />

            <List sx={{ px: 1, py: 1 }}>
                {MENU.map((item) => (
                    <ListItemButton
                        key={item.key}
                        selected={active === item.key}
                        onClick={() => {
                            setActive(item.key)
                            setMobileOpen(false)
                        }}
                        sx={{
                            borderRadius: 2,
                            mx: 1,
                            mb: 0.5,
                            py: { xs: 1.4, md: 1 },
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 42 }}>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.label} primaryTypographyProps={{ sx: { fontWeight: 700 } }} />
                    </ListItemButton>
                ))}
            </List>

            <Divider />

            <Box sx={{ p: 2 }}>
                <Button
                    fullWidth
                    variant="outlined"
                    color="inherit"
                    startIcon={<LogoutOutlinedIcon />}
                    onClick={() => {
                        setMobileOpen(false)
                        handleLogout()
                    }}
                    sx={{ borderRadius: 2.5, py: 1.1, fontWeight: 800 }}
                >
                    Sair
                </Button>

                <Typography variant="caption" sx={{ display: "block", mt: 1.5, opacity: 0.6 }}>
                    {role === "client" ? "Conta de Cliente" : role} • {status}
                </Typography>
            </Box>
        </Box>
    )

    function ProfileHeaderCard() {
        return (
            <Card
                sx={{
                    ...cardSx,
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        p: { xs: 2.4, md: 3 },
                        background:
                            "radial-gradient(1000px 240px at 0% 0%, rgba(0,0,0,0.05), transparent), linear-gradient(135deg, rgba(0,0,0,0.06), transparent)",
                    }}
                >
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={{ xs: 2, sm: 2.5 }}
                        alignItems={{ xs: "stretch", sm: "center" }}
                    >
                        <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            badgeContent={
                                <Tooltip title="Trocar foto">
                                    <span>
                                        <IconButton
                                            disabled={uploadingPhoto || loading}
                                            onClick={() => fileRef.current?.click()}
                                            size="small"
                                            sx={{
                                                bgcolor: "background.paper",
                                                boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                                                "&:hover": { bgcolor: "background.paper" },
                                            }}
                                        >
                                            <PhotoCameraOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            }
                        >
                            {loading ? (
                                <Skeleton variant="circular" width={74} height={74} />
                            ) : (
                                <Avatar
                                    src={profileImage || undefined}
                                    alt={displayName}
                                    sx={{
                                        width: 74,
                                        height: 74,
                                        fontWeight: 700,
                                        fontSize: 26,
                                        border: "1px solid rgba(0,0,0,0.06)",
                                    }}
                                >
                                    {displayName?.[0]?.toUpperCase()}
                                </Avatar>
                            )}
                        </Badge>

                        <Box sx={{ flex: 1 }}>
                            {loading ? (
                                <Stack spacing={1}>
                                    <Skeleton width="48%" />
                                    <Skeleton width="60%" />
                                </Stack>
                            ) : (
                                <>
                                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            {displayName}
                                        </Typography>

                                        <Chip
                                            size="small"
                                            label={status === "active" ? "Conta ativa" : status}
                                            sx={{ borderRadius: 999, fontWeight: 800 }}
                                        />
                                        <Chip
                                            size="small"
                                            label={role === "client" ? "Cliente" : role}
                                            variant="outlined"
                                            sx={{ borderRadius: 999, fontWeight: 800 }}
                                        />
                                    </Stack>

                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.8, flexWrap: "wrap" }}>
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                            {displayEmail}
                                        </Typography>

                                        {displayEmail ? (
                                            <Tooltip title="Copiar e-mail">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        const ok = copyToClipboard(displayEmail)
                                                        toast(ok ? "success" : "error", ok ? "Copiado!" : "Não foi possível copiar.")
                                                    }}
                                                >
                                                    <ContentCopyOutlinedIcon fontSize="inherit" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}
                                    </Stack>
                                </>
                            )}
                        </Box>

                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.2}
                            alignItems="stretch"
                            sx={{ width: { xs: "100%", sm: "auto" } }}
                        >
                            <Button
                                fullWidth
                                variant="contained"
                                sx={{
                                    borderRadius: 2.5,
                                    py: { xs: 0.9, sm: 1.1 },
                                    fontWeight: 700,
                                }}
                                onClick={() => setOpenEditProfile(true)}
                                disabled={loading}
                            >
                                Editar perfil
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                sx={{
                                    borderRadius: 2.5,
                                    py: { xs: 0.9, sm: 1.1 },
                                    fontWeight: 700,
                                }}
                                onClick={() => setOpenEditAddress(true)}
                                disabled={loading}
                            >
                                Editar endereço
                            </Button>
                        </Stack>
                    </Stack>

                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => handlePickPhoto(e.target.files?.[0])}
                    />

                    {uploadingPhoto ? (
                        <Typography variant="caption" sx={{ display: "block", mt: 1.5, opacity: 0.8 }}>
                            Enviando foto…
                        </Typography>
                    ) : null}
                </Box>

                <Divider />

                <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={2} sx={{ display: "flex", justifyContent: "space-around" }}>
                        <Grid item xs={12} md={4}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    boxShadow: {
                                        xs: "0 6px 18px rgba(0,0,0,0.06)",
                                        md: "0 10px 30px rgba(0,0,0,0.06)",
                                    },
                                }}
                            >
                                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                    <Stack direction="row" spacing={1.2} alignItems="center">
                                        <ShoppingBagOutlinedIcon />
                                        <Typography sx={{ fontWeight: 700 }}>Meus pedidos</Typography>
                                    </Stack>
                                    <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.6 }}>
                                        Acompanhe status e histórico.
                                    </Typography>
                                    <Button
                                        fullWidth
                                        sx={{ mt: 1.6, borderRadius: 2.5, fontWeight: 700, py: 1 }}
                                        variant="contained"
                                        onClick={() => navigate("/meus-pedidos")}
                                    >
                                        Ver pedidos
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    boxShadow: {
                                        xs: "0 6px 18px rgba(0,0,0,0.06)",
                                        md: "0 10px 30px rgba(0,0,0,0.06)",
                                    },
                                }}
                            >
                                <CardContent>
                                    <Stack direction="row" spacing={1.2} alignItems="center">
                                        <LocalShippingOutlinedIcon />
                                        <Typography sx={{ fontWeight: 700 }}>Rastreamento</Typography>
                                    </Stack>
                                    <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.6 }}>
                                        Veja entregas em andamento.
                                    </Typography>
                                    <Button
                                        fullWidth
                                        sx={{ mt: 1.6, borderRadius: 2.5, fontWeight: 700, py: 1 }}
                                        variant="outlined"
                                        color="inherit"
                                        onClick={() => navigate("/rastreamento")}
                                    >
                                        Acompanhar
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    boxShadow: {
                                        xs: "0 6px 18px rgba(0,0,0,0.06)",
                                        md: "0 10px 30px rgba(0,0,0,0.06)",
                                    },
                                }}
                            >
                                <CardContent>
                                    <Stack direction="row" spacing={1.2} alignItems="center">
                                        <FavoriteBorderOutlinedIcon />
                                        <Typography sx={{ fontWeight: 700 }}>Favoritos</Typography>
                                    </Stack>
                                    <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.6 }}>
                                        Produtos salvos pra depois.
                                    </Typography>
                                    <Button
                                        fullWidth
                                        sx={{ mt: 1.6, borderRadius: 2.5, fontWeight: 700, py: 1 }}
                                        variant="outlined"
                                        color="inherit"
                                        onClick={() => navigate("/favoritos")}
                                    >
                                        Abrir favoritos
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        )
    }

    // Renamed function to OverviewSection
    function OverviewSection() {
        const wishlist = client?.wishlist || []
        const recentOrders = userOrders.slice(-3)

        return (
            <Stack spacing={{ xs: 3, md: 4 }}>
                <ProfileHeaderCard />

                <Grid container spacing={{ xs: 2, md: 3 }}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Card
                            sx={{
                                ...softCardSx,
                                height: "100%",
                                cursor: "pointer",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: "0 16px 40px rgba(0,0,0,0.1)",
                                },
                            }}
                            onClick={() => setActive("orders")}
                        >
                            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Box
                                        sx={{
                                            bgcolor: "rgba(0,0,0,0.04)",
                                            borderRadius: 3,
                                            p: 1.5,
                                            display: "flex",
                                        }}
                                    >
                                        <ShoppingBagOutlinedIcon sx={{ fontSize: 28 }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                                            {userOrders.length}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7, fontWeight: 600 }}>
                                            Pedidos realizados
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <Card
                            sx={{
                                ...softCardSx,
                                height: "100%",
                                cursor: "pointer",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: "0 16px 40px rgba(0,0,0,0.1)",
                                },
                            }}
                        >
                            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Box
                                        sx={{
                                            bgcolor: "rgba(0,0,0,0.04)",
                                            borderRadius: 3,
                                            p: 1.5,
                                            display: "flex",
                                        }}
                                    >
                                        <FavoriteBorderOutlinedIcon sx={{ fontSize: 28 }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                                            {wishlist.length}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7, fontWeight: 600 }}>
                                            Itens na lista de desejos
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <Card
                            sx={{
                                ...softCardSx,
                                height: "100%",
                                cursor: "pointer",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: "0 16px 40px rgba(0,0,0,0.1)",
                                },
                            }}
                            onClick={() => setActive("address")}
                        >
                            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Box
                                        sx={{
                                            bgcolor: "rgba(0,0,0,0.04)",
                                            borderRadius: 3,
                                            p: 1.5,
                                            display: "flex",
                                        }}
                                    >
                                        <LocalShippingOutlinedIcon sx={{ fontSize: 28 }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                                            {address ? "1" : "0"}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7, fontWeight: 600 }}>
                                            Endereço cadastrado
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {recentOrders.length > 0 && (
                    <Card sx={{ ...softCardSx }}>
                        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                            <SectionHeader title="Pedidos recentes" subtitle="Últimos 3 pedidos realizados" />

                            <Stack spacing={2}>
                                {recentOrders.map((order) => {
                                    const oid = order?.id || "N/A"
                                    const status = order?.status || "pending"
                                    const total = order?.total || 0
                                    const date = order?.createdAt || ""

                                    function formatDate(timestamp) {
                                        if (!timestamp) return "-";
                                        return new Date(timestamp).toLocaleDateString("pt-BR");
                                    }

                                    return (
                                        <Card
                                            key={oid}
                                            sx={{
                                                border: "1px solid rgba(0,0,0,0.06)",
                                                boxShadow: "none",
                                                borderRadius: 3,
                                                transition: "all 0.2s ease",
                                                cursor: "pointer",
                                                "&:hover": {
                                                    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                                                    transform: "translateX(4px)",
                                                },
                                            }}
                                            onClick={() => setActive("orders")}
                                        >
                                            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                                <Stack
                                                    direction={{ xs: "column", sm: "row" }}
                                                    justifyContent="space-between"
                                                    alignItems={{ xs: "flex-start", sm: "center" }}
                                                    spacing={{ xs: 1.5, sm: 2 }}
                                                >
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                            Pedido #{oid}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                                            {formatDate(date)}
                                                        </Typography>
                                                    </Box>

                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        <Chip 
                                                            size="small" 
                                                            label={status === "completed" ? "Concluído" : "Pendente"}
                                                            color={status === "completed" ? "success" : "warning"}
                                                            sx={{ borderRadius: 2, fontWeight: 700 }} 
                                                        />
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                            R$ {total.toFixed(2)}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    )
                                })}
                            </Stack>
                        </CardContent>
                    </Card>
                )}
            </Stack>
        )
    }

    function ProfileSection() {
        return (
            <Stack spacing={{ xs: 3, md: 4 }}>
                <Card sx={{ ...cardSx }}>
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                        <SectionHeader
                            title="Dados pessoais"
                            subtitle="Informações básicas da sua conta"
                            action={
                                <Button
                                    variant="contained"
                                    disableElevation
                                    onClick={() => setOpenEditProfile(true)}
                                    sx={{ borderRadius: 2.5, fontWeight: 800, px: { xs: 3, md: 4 }, py: 1.2 }}
                                >
                                    Editar
                                </Button>
                            }
                        />

                        {loading ? (
                            <Stack spacing={3}>
                                <Skeleton variant="rounded" height={60} />
                                <Skeleton variant="rounded" height={60} />
                            </Stack>
                        ) : (
                            <Grid container spacing={{ xs: 2, md: 3 }}>
                                <Grid item xs={12} md={6}>
                                    <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.6, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}
                                    >
                                        Nome completo
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.8 }}>
                                        {profileForm.name}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.6, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}
                                    >
                                        E-mail
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.8 }}>
                                        {displayEmail}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.6, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}
                                    >
                                        Telefone
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.8 }}>
                                        {profileForm.phone}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.6, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}
                                    >
                                        {profileForm.documentType}
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.8 }}>
                                        {profileForm.documentValue}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            opacity: 0.6,
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            letterSpacing: 0.5,
                                        }}
                                    >
                                        Data de nascimento
                                    </Typography>

                                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.8 }}>
                                        {profileForm.birthDate
                                            ? new Date(profileForm.birthDate).toLocaleDateString("pt-BR")
                                            : "-"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        )}
                    </CardContent>
                </Card>
            </Stack>
        )
    }

    function AddressSection() {
        const address = addresses.find(addr => addr.isDefault) || addresses[0]

        return (
            <Stack spacing={{ xs: 3, md: 4 }}>
                <Card sx={{ ...cardSx }}>
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                        <SectionHeader
                            title="Meus endereços"
                            subtitle="Gerencie seus endereços de entrega"
                            action={
                                <Button
                                    variant="contained"
                                    disableElevation
                                    onClick={() => handleEditAddress(null)}
                                    sx={{ borderRadius: 2.5, fontWeight: 800, px: { xs: 3, md: 4 }, py: 1.2 }}
                                >
                                    Adicionar endereço
                                </Button>
                            }
                        />

                        {loading ? (
                            <Stack spacing={3}>
                                <Skeleton variant="rounded" height={60} />
                                <Skeleton variant="rounded" height={60} />
                            </Stack>
                        ) : addresses.length === 0 ? (
                            <Box
                                sx={{
                                    textAlign: "center",
                                    py: { xs: 4, md: 6 },
                                    px: 2,
                                    bgcolor: "rgba(0,0,0,0.02)",
                                    borderRadius: 3,
                                }}
                            >
                                <HomeOutlinedIcon sx={{ fontSize: 56, opacity: 0.3, mb: 2 }} />
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                    Nenhum endereço cadastrado
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7, mb: 3 }}>
                                    Adicione um endereço para facilitar suas compras
                                </Typography>
                                <Button
                                    variant="contained"
                                    disableElevation
                                    onClick={() => handleEditAddress(null)}
                                    sx={{ borderRadius: 2.5, fontWeight: 800 }}
                                >
                                    Adicionar endereço
                                </Button>
                            </Box>
                        ) : (
                            <Stack spacing={2}>
                                {addresses.map((addr) => (
                                    <Card
                                        key={addr.id}
                                        sx={{
                                            ...softCardSx,
                                            border: addr.isDefault ? "2px solid" : "1px solid rgba(0,0,0,0.06)",
                                            borderColor: addr.isDefault ? "primary.main" : "rgba(0,0,0,0.06)",
                                        }}
                                    >
                                        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                                                        <Chip 
                                                            size="small" 
                                                            label={addr.label || "Casa"} 
                                                            sx={{ borderRadius: 2, fontWeight: 800 }} 
                                                        />
                                                        {addr.isDefault && (
                                                            <Chip 
                                                                size="small" 
                                                                label="Padrão" 
                                                                color="primary"
                                                                sx={{ borderRadius: 2, fontWeight: 800 }} 
                                                            />
                                                        )}
                                                    </Stack>

                                                    <Grid container spacing={{ xs: 1, md: 2 }}>
                                                        {addr.recipientName && (
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="caption" sx={{ opacity: 0.6, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                                                    Destinatário
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                                                                    {addr.recipientName}
                                                                </Typography>
                                                            </Grid>
                                                        )}
                                                        {addr.phone && (
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="caption" sx={{ opacity: 0.6, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                                                    Telefone
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                                                                    {addr.phone}
                                                                </Typography>
                                                            </Grid>
                                                        )}
                                                        <Grid item xs={12}>
                                                            <Typography variant="caption" sx={{ opacity: 0.6, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                                                Endereço
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                                                                {addr.street || "-"}, {addr.number || "S/N"}
                                                                {addr.complement && `, ${addr.complement}`}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
                                                                {addr.neighborhood && `${addr.neighborhood}, `}
                                                                {addr.city && `${addr.city} - `}
                                                                {addr.state && `${addr.state}, `}
                                                                {addr.zipCode && `CEP: ${addr.zipCode}`}
                                                            </Typography>
                                                            {addr.referencePoint && (
                                                                <Typography variant="body2" sx={{ opacity: 0.6, mt: 0.5, fontStyle: "italic" }}>
                                                                    Ref: {addr.referencePoint}
                                                                </Typography>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                                <Stack direction="row" spacing={1}>
                                                    {!addr.isDefault && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => setDefaultAddress(addr.id)}
                                                            sx={{ borderRadius: 2, fontWeight: 700 }}
                                                        >
                                                            Definir como padrão
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => handleEditAddress(addr)}
                                                        sx={{ borderRadius: 2, fontWeight: 700 }}
                                                    >
                                                        Editar
                                                    </Button>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => deleteAddress(addr.id)}
                                                        sx={{ borderRadius: 2 }}
                                                    >
                                                        <DeleteOutlinedIcon />
                                                    </IconButton>
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Stack>
        )
    }

    // Renamed function to SecuritySection
    function SecuritySection() {
        return (
            <Stack spacing={{ xs: 3, md: 4 }}>
                <Card sx={{ ...cardSx }}>
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                        <SectionHeader title="Segurança" subtitle="Gerencie a segurança da sua conta" />

                        <Stack spacing={{ xs: 2, md: 2.5 }}>
                            <Box
                                sx={{
                                    p: { xs: 2.5, md: 3 },
                                    bgcolor: "rgba(0,0,0,0.02)",
                                    borderRadius: 3,
                                    border: "1px solid rgba(0,0,0,0.06)",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        bgcolor: "rgba(0,0,0,0.03)",
                                        borderColor: "rgba(0,0,0,0.12)",
                                    },
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Box
                                        sx={{
                                            bgcolor: "background.paper",
                                            borderRadius: 2,
                                            p: 1.5,
                                            display: "flex",
                                        }}
                                    >
                                        <LockOutlinedIcon sx={{ fontSize: 24 }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.3 }}>
                                            Alterar senha
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                            Atualize sua senha regularmente para manter sua conta segura
                                        </Typography>
                                    </Box>
                                    <Button variant="outlined" sx={{ borderRadius: 2, fontWeight: 700 }}>
                                        Alterar
                                    </Button>
                                </Stack>
                            </Box>

                            <Box
                                sx={{
                                    p: { xs: 2.5, md: 3 },
                                    bgcolor: "rgba(0,0,0,0.02)",
                                    borderRadius: 3,
                                    border: "1px solid rgba(0,0,0,0.06)",
                                }}
                            >
                                <Stack direction="row" alignItems="flex-start" spacing={2}>
                                    <Box
                                        sx={{
                                            bgcolor: "background.paper",
                                            borderRadius: 2,
                                            p: 1.5,
                                            display: "flex",
                                        }}
                                    >
                                        <PersonOutlineIcon sx={{ fontSize: 24 }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.3 }}>
                                            ID de usuário
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7, mb: 1.5 }}>
                                            {uid || "N/A"}
                                        </Typography>
                                        <Button
                                            size="small"
                                            startIcon={<ContentCopyOutlinedIcon />}
                                            onClick={() => {
                                                if (uid && copyToClipboard(uid)) {
                                                    toast("success", "ID copiado!")
                                                }
                                            }}
                                            sx={{ borderRadius: 2, fontWeight: 700 }}
                                        >
                                            Copiar ID
                                        </Button>
                                    </Box>
                                </Stack>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>
        )
    }

    // Renamed function to PreferencesSection
    function PreferencesSection() {
        async function savePrefs() {
            if (!clientPath) return

            try {
                await update(dbRef(rtdb, clientPath), {
                    preferences: {
                        marketing: !!prefs.marketing,
                        orderUpdates: !!prefs.orderUpdates,
                        whatsapp: !!prefs.whatsapp,
                    },
                })

                toast("success", "Preferências atualizadas!")
            } catch {
                toast("error", "Erro ao salvar preferências.")
            }
        }

        return (
            <Stack spacing={{ xs: 3, md: 4 }}>
                <Card sx={{ ...cardSx }}>
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                        <SectionHeader
                            title="Preferências"
                            subtitle="Gerencie suas notificações e comunicações"
                            action={
                                <Button
                                    variant="contained"
                                    disableElevation
                                    onClick={savePrefs}
                                    sx={{ borderRadius: 2.5, fontWeight: 800, px: { xs: 3, md: 4 }, py: 1.2 }}
                                >
                                    Salvar
                                </Button>
                            }
                        />

                        <Stack spacing={2}>
                            <Box
                                sx={{
                                    p: { xs: 2.5, md: 3 },
                                    bgcolor: "rgba(0,0,0,0.02)",
                                    borderRadius: 3,
                                    border: "1px solid rgba(0,0,0,0.06)",
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={prefs.marketing}
                                            onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                E-mails promocionais
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.3 }}>
                                                Receba ofertas, novidades e promoções exclusivas
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>

                            <Box
                                sx={{
                                    p: { xs: 2.5, md: 3 },
                                    bgcolor: "rgba(0,0,0,0.02)",
                                    borderRadius: 3,
                                    border: "1px solid rgba(0,0,0,0.06)",
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={prefs.orderUpdates}
                                            onChange={(e) => setPrefs({ ...prefs, orderUpdates: e.target.checked })}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Atualizações de pedidos
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.3 }}>
                                                Seja notificado sobre o status dos seus pedidos
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>

                            <Box
                                sx={{
                                    p: { xs: 2.5, md: 3 },
                                    bgcolor: "rgba(0,0,0,0.02)",
                                    borderRadius: 3,
                                    border: "1px solid rgba(0,0,0,0.06)",
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={prefs.whatsapp}
                                            onChange={(e) => setPrefs({ ...prefs, whatsapp: e.target.checked })}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Notificações por WhatsApp
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.3 }}>
                                                Receba atualizações importantes via WhatsApp
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>
        )
    }

    // Renamed function to SupportSection
    function SupportSection() {
        return (
            <Stack spacing={{ xs: 3, md: 4 }}>
                <Card sx={{ ...cardSx }}>
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                        <SectionHeader title="Suporte" subtitle="Precisa de ajuda? Entre em contato conosco" />

                        <Grid container spacing={{ xs: 2, md: 3 }}>
                            <Grid item xs={12} md={6}>
                                <Box
                                    sx={{
                                        p: { xs: 2.5, md: 3 },
                                        bgcolor: "rgba(0,0,0,0.02)",
                                        borderRadius: 3,
                                        border: "1px solid rgba(0,0,0,0.06)",
                                        height: "100%",
                                        transition: "all 0.2s ease",
                                        cursor: "pointer",
                                        "&:hover": {
                                            bgcolor: "rgba(0,0,0,0.03)",
                                            borderColor: "rgba(0,0,0,0.12)",
                                            transform: "translateY(-2px)",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            bgcolor: "background.paper",
                                            borderRadius: 2,
                                            p: 1.5,
                                            display: "inline-flex",
                                            mb: 2,
                                        }}
                                    >
                                        <SupportAgentOutlinedIcon sx={{ fontSize: 32 }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                        Central de ajuda
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
                                        Acesse nossa central de ajuda com perguntas frequentes e tutoriais
                                    </Typography>
                                    <Button variant="outlined" sx={{ borderRadius: 2, fontWeight: 700 }}>
                                        Acessar
                                    </Button>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box
                                    sx={{
                                        p: { xs: 2.5, md: 3 },
                                        bgcolor: "rgba(0,0,0,0.02)",
                                        borderRadius: 3,
                                        border: "1px solid rgba(0,0,0,0.06)",
                                        height: "100%",
                                        transition: "all 0.2s ease",
                                        cursor: "pointer",
                                        "&:hover": {
                                            bgcolor: "rgba(0,0,0,0.03)",
                                            borderColor: "rgba(0,0,0,0.12)",
                                            transform: "translateY(-2px)",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            bgcolor: "background.paper",
                                            borderRadius: 2,
                                            p: 1.5,
                                            display: "inline-flex",
                                            mb: 2,
                                        }}
                                    >
                                        <SupportAgentOutlinedIcon sx={{ fontSize: 32 }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                        Fale conosco
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
                                        Entre em contato direto com nossa equipe de suporte
                                    </Typography>
                                    <Button variant="contained" disableElevation sx={{ borderRadius: 2, fontWeight: 700 }}>
                                        Contatar
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Stack>
        )
    }

    function OrdersSection() {
        function formatDate(timestamp) {
            if (!timestamp) return "Data inválida";
            const date = new Date(timestamp);
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            return `${day}/${month}/${year} às ${hours}:${minutes}`;
        }

        function getPaymentMethod(payment) {
            if (!payment || !payment.method) return "N/A";
            return payment.method === "card" ? "Cartão de crédito" : "PIX";
        }

        function getStatusLabel(status) {
            return status === "completed" ? "Concluído" : "Pendente";
        }

        return (
            <Stack spacing={{ xs: 3, md: 4 }}>
                <Card sx={{ ...cardSx }}>
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                        <SectionHeader
                            title="Meus pedidos"
                            subtitle="Histórico completo de seus pedidos"
                        />
                    </CardContent>
                </Card>

                {loadingOrders ? (
                    <Card sx={{ ...cardSx }}>
                        <CardContent sx={{ p: { xs: 2.5, md: 4 }, textAlign: "center" }}>
                            <CircularProgress />
                        </CardContent>
                    </Card>
                ) : userOrders.length === 0 ? (
                    <Card sx={{ ...cardSx }}>
                        <CardContent sx={{ p: { xs: 2.5, md: 4 }, textAlign: "center" }}>
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                                Nenhum pedido encontrado
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                Você ainda não realizou nenhum pedido.
                            </Typography>
                        </CardContent>
                    </Card>
                ) : (
                    <Stack spacing={2}>
                        {userOrders.map((order) => (
                            <Card
                                key={order.id}
                                sx={{
                                    ...softCardSx,
                                    "&:hover": {
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 12px 30px rgba(0,0,0,0.1)",
                                    },
                                }}
                            >
                                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                                    <Stack spacing={2}>
                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            justifyContent="space-between"
                                            alignItems={{ xs: "flex-start", sm: "center" }}
                                            spacing={2}
                                        >
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                    Pedido #{order.id}
                                                </Typography>
                                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                                    {formatDate(order.createdAt)}
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Chip
                                                    label={getStatusLabel(order.status)}
                                                    color={order.status === "completed" ? "success" : "warning"}
                                                    size="small"
                                                    sx={{ borderRadius: 2, fontWeight: 700 }}
                                                />
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                    R$ {order.total?.toFixed(2) || "0.00"}
                                                </Typography>
                                            </Stack>
                                        </Stack>

                                        <Divider />

                                        <Stack spacing={1}>
                                            <InfoRow label="Forma de pagamento" value={getPaymentMethod(order.payment)} />
                                            {order.payment?.method === "card" && order.payment?.installments && (
                                                <InfoRow label="Parcelamento" value={`${order.payment.installments}x`} />
                                            )}
                                            {order.shipping?.carrier && (
                                                <InfoRow label="Transportadora" value={order.shipping.carrier} />
                                            )}
                                            {order.shipping?.prazo && (
                                                <InfoRow label="Prazo de entrega" value={order.shipping.prazo} />
                                            )}
                                        </Stack>

                                        {order.items && order.items.length > 0 && (
                                            <>
                                                <Divider />
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                                    Itens do pedido:
                                                </Typography>
                                                <Stack spacing={1}>
                                                    {order.items.map((item, index) => (
                                                        <Box key={index}>
                                                            <Typography variant="body2">
                                                                {item.productName} - {item.flavor} (Qtd: {item.quantity})
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                                                R$ {item.price.toFixed(2)} cada
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </>
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}
            </Stack>
        );
    }

    function InfoRow({ label, value }) {
        return (
            <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    {label}:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {value}
                </Typography>
            </Stack>
        );
    }

    function Content() {
        switch (active) {
            case "overview":
                return <OverviewSection /> // Use the renamed component
            case "profile":
                return <ProfileSection />
            case "address":
                return <AddressSection />
            case "orders":
                return <OrdersSection />
            case "security":
                return <SecuritySection /> // Use the renamed component
            case "prefs":
                return <PreferencesSection /> // Use the renamed component
            case "support":
                return <SupportSection /> // Use the renamed component
            default:
                return <OverviewSection /> // Use the renamed component
        }
    }

    if (!uid && !loading) {
        return (
            <Container maxWidth="md" sx={{ py: 6 }}>
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                    Não encontrei seu usuário logado. Volte para o login.
                </Alert>
                <Button
                    onClick={() => navigate("/auth")}
                    sx={{ mt: 2, borderRadius: 2.5, fontWeight: 700 }}
                    variant="contained"
                >
                    Ir para Login
                </Button>
            </Container>
        )
    }

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa", display: "flex", justifyContent: "center", width: "100%" }}>
            <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, display: "flex", justifyContent: "center" }}>
                <Grid container spacing={{ xs: 0, md: 3 }}>
                    {/* Sidebar Desktop */}
                    <Grid
                        item
                        md={3.5}
                        lg={3}
                        sx={{
                            display: { xs: "none", md: "block" },
                        }}
                    >
                        <Box sx={{ position: "sticky", top: 24 }}>
                            <Card sx={{ ...cardSx }}>{sidebar}</Card>
                        </Box>
                    </Grid>

                    {/* Mobile Header */}
                    <Grid item xs={12} sx={{ display: { xs: "block", md: "none" }, mb: 2, width: "100%" }}>
                        <Card sx={{ ...cardSx }}>
                            <CardContent sx={{ p: 2 }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            Minha Conta
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                            {MENU.find((m) => m.key === active)?.label || ""}
                                        </Typography>
                                    </Box>
                                    <IconButton onClick={toggleMobile} size="large">
                                        <MenuOutlinedIcon />
                                    </IconButton>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Main Content */}
                    <Grid item xs={12} md={8.5} lg={9} sx={{ width: "850px" }}>
                        <Content />
                    </Grid>
                </Grid>
            </Container>

            {/* Mobile Drawer */}
            <Drawer
                anchor="left"
                open={mobileOpen}
                onClose={toggleMobile}
                sx={{
                    display: { xs: "block", md: "none" },
                    "& .MuiDrawer-paper": { width: 280 },
                }}
            >
                {sidebar}
            </Drawer>

            {/* Dialogs */}
            <Dialog
                open={openEditProfile}
                onClose={() => setOpenEditProfile(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: 22 }}>Editar dados pessoais</DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        <TextField
                            label="Nome completo"
                            fullWidth
                            value={profileForm.name}
                            onChange={(e) =>
                                setProfileForm({ ...profileForm, name: e.target.value })
                            }
                            sx={textFieldSx}
                        />

                        <TextField
                            label="E-mail"
                            fullWidth
                            disabled
                            value={profileForm.email}
                            helperText="E-mail não pode ser alterado"
                            sx={textFieldSx}
                        />

                        <TextField
                            label="Telefone"
                            fullWidth
                            value={profileForm.phone}
                            onChange={(e) =>
                                setProfileForm({
                                    ...profileForm,
                                    phone: sanitizePhone(e.target.value),
                                })
                            }
                            sx={textFieldSx}
                        />

                        <TextField
                            label="Data de nascimento"
                            fullWidth
                            disabled
                            value={
                                profileForm.birthDate
                                    ? new Date(profileForm.birthDate).toLocaleDateString("pt-BR")
                                    : "-"
                            }
                            helperText="Data de nascimento não pode ser alterada"
                            sx={textFieldSx}
                        />

                        <TextField
                            label="CPF"
                            fullWidth
                            disabled
                            value={profileForm.documentValue}
                            helperText="CPF não pode ser alterado"
                            sx={textFieldSx}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 2 }}>
                    <Button onClick={() => setOpenEditProfile(false)} sx={{ borderRadius: 2, fontWeight: 700 }}>
                        Cancelar
                    </Button>
                    <Button onClick={saveProfile} variant="contained" disableElevation sx={{ borderRadius: 2, fontWeight: 800 }}>
                        Salvar alterações
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openEditAddress}
                onClose={() => setOpenEditAddress(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: 22 }}>Editar endereço de entrega</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2.5} sx={{ mt: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Nome do endereço"
                                fullWidth
                                value={addressForm.label}
                                onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                                placeholder="Ex: Casa, Trabalho"
                                sx={textFieldSx}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Nome do destinatário"
                                fullWidth
                                value={addressForm.recipientName}
                                onChange={(e) =>
                                    setAddressForm({
                                        ...addressForm,
                                        recipientName: e.target.value,
                                    })
                                }
                                sx={textFieldSx}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Telefone para contato"
                                fullWidth
                                value={addressForm.phone}
                                onChange={(e) =>
                                    setAddressForm({
                                        ...addressForm,
                                        phone: sanitizePhone(e.target.value),
                                    })
                                }
                                sx={textFieldSx}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="CEP"
                                fullWidth
                                value={addressForm.zipCode}
                                onChange={(e) => {
                                    const valor = e.target.value.replace(/\D/g, "")
                                    // Formata CEP: 12345678 -> 12345-678
                                    const cepFormatado = valor.length > 5 
                                        ? `${valor.slice(0, 5)}-${valor.slice(5, 8)}`
                                        : valor
                                    setAddressForm({
                                        ...addressForm,
                                        zipCode: cepFormatado,
                                    })
                                }}
                                placeholder="00000-000"
                                inputProps={{ maxLength: 9 }}
                                sx={textFieldSx}
                                InputProps={{
                                    endAdornment: loadingCEP ? (
                                        <InputAdornment position="end">
                                            <CircularProgress size={20} />
                                        </InputAdornment>
                                    ) : addressForm.zipCode?.replace(/\D/g, "").length === 8 ? (
                                        <InputAdornment position="end">
                                            <SearchIcon color="primary" />
                                        </InputAdornment>
                                    ) : null,
                                }}
                                helperText={addressForm.zipCode?.replace(/\D/g, "").length === 8 
                                    ? "Endereço encontrado automaticamente" 
                                    : "Digite o CEP para buscar o endereço"}
                            />
                        </Grid>

                        <Grid item xs={12} sm={8}>
                            <TextField
                                label="Rua / Avenida"
                                fullWidth
                                value={addressForm.street}
                                onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })}
                                sx={textFieldSx}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Número"
                                fullWidth
                                value={addressForm.number}
                                onChange={(e) => setAddressForm({ ...addressForm, number: e.target.value })}
                                sx={textFieldSx}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Complemento"
                                fullWidth
                                value={addressForm.complement}
                                onChange={(e) => setAddressForm({ ...addressForm, complement: e.target.value })}
                                placeholder="Apto, Bloco, etc."
                                sx={textFieldSx}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Bairro"
                                fullWidth
                                value={addressForm.neighborhood}
                                onChange={(e) =>
                                    setAddressForm({
                                        ...addressForm,
                                        neighborhood: e.target.value,
                                    })
                                }
                                sx={textFieldSx}
                            />
                        </Grid>

                        <Grid item xs={12} sm={8}>
                            <TextField
                                label="Cidade"
                                fullWidth
                                value={addressForm.city}
                                onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                                sx={textFieldSx}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Estado"
                                fullWidth
                                value={addressForm.state}
                                onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                                placeholder="UF"
                                sx={textFieldSx}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Ponto de referência"
                                fullWidth
                                value={addressForm.referencePoint}
                                onChange={(e) =>
                                    setAddressForm({
                                        ...addressForm,
                                        referencePoint: e.target.value,
                                    })
                                }
                                placeholder="Próximo a..."
                                sx={textFieldSx}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={addressForm.isDefault}
                                        onChange={(e) =>
                                            setAddressForm({
                                                ...addressForm,
                                                isDefault: e.target.checked,
                                            })
                                        }
                                    />
                                }
                                label="Definir como endereço padrão"
                                sx={{ mt: 1 }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 2 }}>
                    <Button 
                        onClick={() => {
                            setOpenEditAddress(false)
                            setEditingAddressId(null)
                            setAddressForm({
                                label: "Casa",
                                recipientName: "",
                                phone: "",
                                street: "",
                                number: "",
                                complement: "",
                                neighborhood: "",
                                city: "",
                                state: "",
                                zipCode: "",
                                country: "BR",
                                referencePoint: "",
                                isDefault: false,
                            })
                        }} 
                        sx={{ borderRadius: 2, fontWeight: 700 }}
                    >
                        Cancelar
                    </Button>
                    <Button onClick={saveAddress} variant="contained" disableElevation sx={{ borderRadius: 2, fontWeight: 800 }}>
                        {editingAddressId ? "Atualizar endereço" : "Salvar endereço"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Hidden file input */}
            <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handlePickPhoto(file)
                }}
            />

            {/* Snackbar */}
            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack({ ...snack, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snack.type}
                    variant="filled"
                    onClose={() => setSnack({ ...snack, open: false })}
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </Box>
    )
}
